import type { Position, Direction } from "../types/grid.js";
import {
  snapshotStores,
  restoreStores,
  type StoreSnapshot,
} from "./StoreSnapshot.js";
import { PartyStore } from "../../store/PartyStore.js";
import { WorldStore } from "../../store/WorldStore.js";
import { getActiveAdventure } from "../adventure/ActiveAdventure.js";

// ── Versioning ────────────────────────────────────────────────────────────────

/**
 * Bump every time the on-disk state surface changes shape.
 * When you bump, register a migration from the previous number to this one.
 */
export const CURRENT_SAVE_VERSION = 2;

// ── State surface ─────────────────────────────────────────────────────────────

/** Where the party currently stands in the world (P02). */
export interface WorldPointer {
  readonly mapId: string;
  readonly position: Position;
  /** Facing as a `Direction` enum value (radians — see grid.ts). */
  readonly facing: Direction;
}

/**
 * The full snapshot the engine needs to resume mid-dungeon.
 * Aggregates the per-store snapshots plus the runtime cross-cuts that
 * don't belong to any single store (Ink blob, world pointer, optional RNG seed).
 */
export interface SaveState extends StoreSnapshot {
  /**
   * Opaque inkjs runtime blob from `story.state.ToJson()`.
   * Restored verbatim via `story.state.LoadJson(...)`.
   * `null` when no story is currently bound (e.g. fresh-game saves).
   */
  readonly ink: string | null;
  readonly pointer: WorldPointer;
  /**
   * Optional seed captured at snapshot time. Not used during normal load
   * (a loaded game diverges from its save-moment), but useful for tests
   * and bug repros (P15).
   */
  readonly rngSeed?: number;
}

// ── Header / metadata ─────────────────────────────────────────────────────────

/**
 * Shown in the save-slot UI without parsing the full state blob.
 * Mirrors fields that already live elsewhere — duplicating them here lets
 * the save list render quickly without rehydrating every store.
 */
export interface SaveMetadata {
  /** ISO-8601 wall-clock timestamp the save was written. */
  readonly timestamp: string;
  /** Mirrored from `PartyStore.getPlaytimeMs()` at save time. */
  readonly playtimeMs: number;
  /** Human-readable floor label (e.g. `"The Mossy Crypt"`). */
  readonly floorName: string;
  /** Party summary for the save-slot list. */
  readonly party: readonly { readonly name: string; readonly level: number }[];
  /**
   * Stable identifier for the party that owns this save. Minted at chargen
   * (or fork) and carried verbatim through every subsequent save. The
   * launch-screen save picker groups by `(adventureId, partyId)` — see
   * `specs/persistence.md` §"Party identity on the save".
   */
  readonly partyId: string;
  /** Player-entered name, defaults to `"Party of ${firstName}"`. */
  readonly partyName: string;
}

/**
 * On-disk save file. Persistence layer writes this verbatim into IndexedDB
 * (see P04 `IDBStore`) and reads it back through `loadSaveFile`.
 *
 * The four top-level keys are the **versioned header** the spec calls for:
 * `version` + `adventureId` + `adventureVersion` gate compatibility before
 * any of `state` is parsed.
 */
export interface SaveFile {
  readonly version: number;
  readonly adventureId: string;
  readonly adventureVersion: string;
  readonly meta: SaveMetadata;
  readonly state: SaveState;
}

// ── Errors ────────────────────────────────────────────────────────────────────

export class SaveVersionError extends Error {
  constructor(
    readonly fromVersion: number,
    readonly toVersion: number,
  ) {
    // Direction matters: older-than-current means a migration is missing;
    // newer-than-current means the user opened a save from a later build.
    // Both are blocking, but the message should reflect which case hit.
    const message =
      fromVersion > toVersion
        ? `This save is from a newer version of the game (v${String(fromVersion)} > v${String(toVersion)}); update the client to load it.`
        : `This save is from an older version of the game (v${String(fromVersion)} → v${String(toVersion)}); no migration is registered.`;
    super(message);
    this.name = "SaveVersionError";
  }
}

export class AdventureMismatchError extends Error {
  constructor(
    readonly expected: string,
    readonly actual: string,
  ) {
    super(
      `This save is from a different adventure (expected "${expected}", got "${actual}").`,
    );
    this.name = "AdventureMismatchError";
  }
}

export class SaveSchemaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SaveSchemaError";
  }
}

// ── Migration registry ────────────────────────────────────────────────────────

/**
 * Each migration converts a save blob from `fromVersion` to `fromVersion + 1`.
 * Operate on the unparsed `unknown` payload — older shapes won't satisfy the
 * current `SaveFile` interface, and that's the point.
 */
export type SaveMigration = (raw: unknown) => unknown;

const migrations = new Map<number, SaveMigration>();

/**
 * Register a migration from one version to the next. Idempotent — re-registering
 * the same `fromVersion` overwrites the prior entry (handy in tests).
 */
export function registerSaveMigration(
  fromVersion: number,
  migrate: SaveMigration,
): void {
  migrations.set(fromVersion, migrate);
}

/**
 * Drop every registered migration and re-register the built-ins. Intended
 * for test teardown — tests that exercise the migration registry expect a
 * clean slate, but built-ins are part of the engine contract and must
 * survive reset so later tests against `migrateSaveFile` keep working.
 */
export function _resetSaveMigrations(): void {
  migrations.clear();
  registerBuiltInMigrations();
}

/**
 * Run every registered migration in order until the blob reports
 * `version === CURRENT_SAVE_VERSION`.
 *
 * Throws `SaveVersionError` if a hop is missing (e.g. v1 → v3 with no v2 migration),
 * or `SaveSchemaError` if the input is structurally invalid.
 */
export function migrateSaveFile(raw: unknown): SaveFile {
  if (!isObject(raw) || typeof raw.version !== "number") {
    throw new SaveSchemaError("Save blob is missing a numeric `version` header.");
  }
  let cursor: unknown = raw;
  let version = raw.version;
  while (version < CURRENT_SAVE_VERSION) {
    const step = migrations.get(version);
    if (!step) throw new SaveVersionError(version, CURRENT_SAVE_VERSION);
    cursor = step(cursor);
    if (!isObject(cursor) || typeof cursor.version !== "number") {
      throw new SaveSchemaError(
        `Migration from v${String(version)} returned an invalid blob.`,
      );
    }
    if (cursor.version <= version) {
      throw new SaveSchemaError(
        `Migration from v${String(version)} did not advance the version field.`,
      );
    }
    version = cursor.version;
  }
  if (version > CURRENT_SAVE_VERSION) {
    throw new SaveVersionError(version, CURRENT_SAVE_VERSION);
  }
  return cursor as SaveFile;
}

// ── Build / load ──────────────────────────────────────────────────────────────

export interface BuildSaveFileInput {
  readonly adventureId: string;
  readonly adventureVersion: string;
  readonly floorName: string;
  readonly pointer: WorldPointer;
  readonly inkState: string | null;
  readonly rngSeed?: number;
  /** Override the wall-clock timestamp; defaults to `new Date().toISOString()`. */
  readonly timestamp?: string;
  /**
   * Optional override for party identity. Defaults to the live
   * `PartyStore` values; tests pass explicit values to avoid touching
   * the store singleton.
   */
  readonly partyId?: string;
  readonly partyName?: string;
}

/**
 * Capture the live engine state (party, world, session, ink, pointer) into a
 * new `SaveFile` at the current schema version. The caller is responsible for
 * supplying the adventure binding and the floor's display name — both come
 * from data the persistence layer doesn't know about.
 */
export function buildSaveFile(input: BuildSaveFileInput): SaveFile {
  const stores = snapshotStores();
  const members = PartyStore.getMembers();
  const party = members.map((m) => ({
    name: m.name,
    level: m.level,
  }));
  const memberNames = members.map((m) => m.name);
  const partyId =
    input.partyId ?? PartyStore.getPartyId() ?? synthesizePartyId(memberNames);
  const partyName =
    input.partyName ?? PartyStore.getPartyName() ?? defaultPartyName(memberNames);
  const meta: SaveMetadata = {
    timestamp: input.timestamp ?? new Date().toISOString(),
    playtimeMs: PartyStore.getPlaytimeMs(),
    floorName: input.floorName,
    party,
    partyId,
    partyName,
  };
  const state: SaveState = {
    ...stores,
    ink: input.inkState,
    pointer: input.pointer,
    ...(input.rngSeed !== undefined ? { rngSeed: input.rngSeed } : {}),
  };
  return {
    version: CURRENT_SAVE_VERSION,
    adventureId: input.adventureId,
    adventureVersion: input.adventureVersion,
    meta,
    state,
  };
}

export interface LoadSaveFileOptions {
  readonly expectedAdventureId: string;
  readonly currentAdventureVersion: string;
}

// ── Adventure-scoped convenience (Z05) ────────────────────────────────────────

export interface BuildSaveFileForActiveAdventureInput {
  readonly floorName: string;
  readonly pointer: WorldPointer;
  readonly inkState: string | null;
  readonly rngSeed?: number;
  readonly timestamp?: string;
}

/**
 * Convenience over `buildSaveFile` that pulls `adventureId` + `adventureVersion`
 * from the mounted `ActiveAdventure` manifest, so save-call sites don't have
 * to thread them through. Z05: save format is keyed on the active adventure.
 */
export function buildSaveFileForActiveAdventure(
  input: BuildSaveFileForActiveAdventureInput,
): SaveFile {
  const { manifest } = getActiveAdventure();
  return buildSaveFile({
    adventureId: manifest.id,
    adventureVersion: manifest.version,
    ...input,
  });
}

/**
 * Convenience over `loadSaveFile` that resolves the expected adventure ID +
 * current version from the mounted `ActiveAdventure` manifest. Throws
 * `AdventureMismatchError` if the save was written under a different
 * adventure (Z05 + P13).
 */
export function loadSaveFileForActiveAdventure(raw: unknown): LoadSaveFileResult {
  const { manifest } = getActiveAdventure();
  return loadSaveFile(raw, {
    expectedAdventureId: manifest.id,
    currentAdventureVersion: manifest.version,
  });
}

export interface LoadSaveFileResult {
  readonly file: SaveFile;
  /**
   * Set when the save's `adventureVersion` differs from the current one.
   * Spec: load proceeds with a warning rather than refusing.
   */
  readonly adventureVersionWarning: string | null;
}

/**
 * Validate, migrate, and apply a raw save blob. Replaces every store's
 * state wholesale via `restoreStores`. Does **not** rebind the Ink runtime
 * or rebuild the scene — the caller owns those steps.
 *
 * Throws on hard incompatibility (`SaveVersionError`, `AdventureMismatchError`,
 * `SaveSchemaError`); returns a `LoadSaveFileResult` with a soft warning when
 * the adventure version differs but the IDs match.
 */
export function loadSaveFile(
  raw: unknown,
  options: LoadSaveFileOptions,
): LoadSaveFileResult {
  const file = migrateSaveFile(raw);
  if (file.adventureId !== options.expectedAdventureId) {
    throw new AdventureMismatchError(options.expectedAdventureId, file.adventureId);
  }
  const adventureVersionWarning =
    file.adventureVersion !== options.currentAdventureVersion
      ? `Save was written under adventure version "${file.adventureVersion}"; current version is "${options.currentAdventureVersion}". Loading anyway.`
      : null;
  restoreStores({
    party: file.state.party,
    world: file.state.world,
    session: file.state.session,
  });
  // Make sure the world pointer's map is the active one after restore — the
  // snapshot's `world.currentMapId` already does this, but the pointer is
  // authoritative for resuming gameplay (it carries position + facing too).
  WorldStore.setCurrentMap(file.state.pointer.mapId);
  return { file, adventureVersionWarning };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// ── Party identity helpers ────────────────────────────────────────────────────

/**
 * Deterministic default party name used when no player-entered name is
 * available — for the chargen auto-suggest and for v1→v2 migration.
 */
export function defaultPartyName(memberNames: readonly string[]): string {
  const first = memberNames.find((n) => n.trim().length > 0);
  if (first === undefined) return "Untitled Party";
  return `Party of ${first.trim()}`;
}

/**
 * Stable hash of a party roster's member names, used as a synthesized
 * `partyId` when a pre-v2 save is migrated and has no real UUID on file.
 * The goal is cross-session stability (two saves from the same pre-v2
 * roster derive the same id, so grouping works) — not cryptographic
 * resistance. djb2 over the canonical-joined names is plenty.
 */
export function synthesizePartyId(memberNames: readonly string[]): string {
  const canonical = memberNames.map((n) => n.trim().toLowerCase()).join("|");
  let hash = 5381;
  for (let i = 0; i < canonical.length; i += 1) {
    hash = ((hash << 5) + hash + canonical.charCodeAt(i)) | 0;
  }
  // Unsigned 32-bit, base-36; prefixed so it's obvious these are derived.
  return `legacy-${(hash >>> 0).toString(36)}`;
}

// ── Built-in migrations ───────────────────────────────────────────────────────

/**
 * v1 → v2: add `meta.partyId` and `meta.partyName`. Earlier saves never
 * carried party identity, so we synthesize both from the stored party
 * summary — the member-name hash keeps grouping stable across re-migrations
 * of the same save, and the default name is friendlier than an opaque UUID.
 */
function migrateV1ToV2(raw: unknown): unknown {
  if (!isObject(raw)) {
    throw new SaveSchemaError("v1 save blob is not an object.");
  }
  const meta = isObject(raw.meta) ? raw.meta : {};
  const partyArray = Array.isArray(meta.party) ? meta.party : [];
  const memberNames = partyArray
    .map((p) => (isObject(p) && typeof p.name === "string" ? p.name : ""))
    .filter((n) => n.length > 0);
  return {
    ...raw,
    version: 2,
    meta: {
      ...meta,
      partyId:
        typeof meta.partyId === "string" ? meta.partyId : synthesizePartyId(memberNames),
      partyName:
        typeof meta.partyName === "string"
          ? meta.partyName
          : defaultPartyName(memberNames),
    },
  };
}

function registerBuiltInMigrations(): void {
  registerSaveMigration(1, migrateV1ToV2);
}

registerBuiltInMigrations();
